import { isValidEmail } from '../../../utils/validation';
/** Create a regex validator that can be used as a validator function
 *
 * @param regex regexp to match a string
 * @param message Error message to show if regexp does not match
 *
 * @returns A Validator function
 */
export const createRegexValidator = (regex, message) => {
    return (value) => {
        return !regex.test(value) ? message : undefined;
    };
};
/** @ignore
 *
 * Regexes used by validators.
 *
 * Can be reused in a Validator function if needed, though it's
 * typically easier to use the specific validator functions directly.
 */
export const VALIDATION_REGEXES = {
    /** matches non-empty string */
    REQUIRED: /\S/,
    /**
     * @deprecated the regex does not cover all valid or invalid emails use the isValidEmail function
     */
    EMAIL: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    /** Matches UK phone number */
    UK_PHONE: /^\(?( *\d\)?){11,14}$/,
    /** Matches UK post code */
    UK_POSTCODE: /(GIR 0AA|[A-PR-UWYZ]([0-9]{1,2}|([A-HK-Y][0-9]|[A-HK-Y][0-9]([0-9]|[ABEHMNPRV-Y]))|[0-9][A-HJKPS-UW]) {0,1}[0-9][ABD-HJLNP-UW-Z]{2})$/i,
    ADDRESSLINE: /^[0-9a-zA-Z\s-',.]+$/i,
};
/** Used by the required validator.
 *
 * Can be used if you need something similar in your Validator function.
 */
export const REQUIRED_MESSAGE = 'This field is required.';
export const INVALID_EMAIL_MESSAGE = 'Invalid email address.';
const stringRequiredValidator = createRegexValidator(VALIDATION_REGEXES.REQUIRED, REQUIRED_MESSAGE);
export const createRequiredValidator = () => {
    return (value) => {
        // handle any case where value is null or undefined
        if (value == null) {
            return REQUIRED_MESSAGE;
        }
        // in case of string type, we want to use the string validator
        if (typeof value === 'string') {
            return stringRequiredValidator(value);
        }
        if (value instanceof Set && value.size === 0) {
            return REQUIRED_MESSAGE;
        }
        if (Array.isArray(value) && value.length === 0) {
            return REQUIRED_MESSAGE;
        }
        // value is not null or undefined or a string, so it's fine
        return undefined;
    };
};
/** Validate email address.
 *
 * Can be used as a validator in a schema
 */
export const emailValidator = (value) => {
    return isValidEmail(value) ? undefined : INVALID_EMAIL_MESSAGE;
};
/** Validate UK phone number
 *
 * Can be used as a validator in a schema
 */
export const ukPhoneValidator = createRegexValidator(VALIDATION_REGEXES.UK_PHONE, 'Invalid phone number format. Expected format is: 44 791 1123456');
/** Validate UK postcode
 *
 * Can be used as a validator in a schema
 */
export const ukPostcodeValidator = (value) => {
    return !VALIDATION_REGEXES.UK_POSTCODE.test(value.replace(/ /g, ''))
        ? 'Invalid postcode.'
        : undefined;
};
export const composeValidators = (validators) => {
    const filteredValidators = validators.filter((validator) => validator != null);
    return (value) => {
        for (const element of filteredValidators) {
            const message = element(value);
            if (message != null) {
                return message;
            }
        }
        return undefined;
    };
};
export const normalizeValidator = (validator) => {
    if (Array.isArray(validator)) {
        return composeValidators(validator);
    }
    if (validator == null) {
        return noopValidator;
    }
    return validator;
};
export const noopValidator = () => undefined;
export const addressLineValidator = (address) => {
    return address && !VALIDATION_REGEXES.ADDRESSLINE.test(address)
        ? 'Address contains invalid characters.'
        : undefined;
};
